import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { logAudit, AUDIT_ACTIONS } from '../../utils/audit';
import { IconArrowLeft, IconPlus, IconGripVertical, IconTrash, IconX } from '@tabler/icons-react';

const FAIL_BEHAVIOURS = [
  { key: 'none', label: 'Log only', desc: 'Recorded in history. No alert sent.', colour: 'var(--color-text-tertiary)' },
  { key: 'flag', label: 'Log and notify', desc: 'Manager receives a push notification.', colour: 'var(--color-warning)' },
  { key: 'block', label: 'Log, notify and block', desc: 'Manager notified. Asset blocked immediately.', colour: 'var(--color-danger)' },
];

const ITEM_TYPES = [
  { key: 'passfail', label: 'Pass / Fail' },
  { key: 'number', label: 'Number input' },
  { key: 'text', label: 'Text input' },
  { key: 'photo', label: 'Photo required' },
];

export default function TemplateEditor() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { orgId, userProfile } = useAuth();
  const isNew = templateId === 'new';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetTypes, setAssetTypes] = useState(['vehicle']);
  const [estimatedMinutes, setEstimatedMinutes] = useState(5);
  const [frequency, setFrequency] = useState('daily');
  const [dueByTime, setDueByTime] = useState('08:00');
  const [sections, setSections] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    if (isNew) {
      setSections([{ id: 's1', title: 'Section 1', items: [] }]);
      return;
    }
    const load = async () => {
      if (!resolvedOrgId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', resolvedOrgId, 'checkTemplates', templateId));
        if (snap.exists()) {
          const data = snap.data();
          setName(data.name || '');
          setDescription(data.description || '');
          setAssetTypes(data.assetTypes || ['vehicle']);
          setEstimatedMinutes(data.estimatedMinutes || 5);
          setFrequency(data.frequency || 'daily');
          setDueByTime(data.dueByTime || '08:00');
          setSections(data.sections || []);
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [templateId, resolvedOrgId, isNew]);

  const addSection = () => {
    const id = `s${Date.now()}`;
    setSections(prev => [...prev, { id, title: 'New section', items: [] }]);
  };

  const updateSectionTitle = (sectionId, title) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, title } : s));
  };

  const deleteSection = (sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
    if (selectedItem?.sectionId === sectionId) setSelectedItem(null);
  };

  const addItem = (sectionId) => {
    const itemId = `i${Date.now()}`;
    const newItem = { id: itemId, label: 'New check item', failBehaviour: 'flag', required: true, type: 'passfail' };
    setSections(prev => prev.map(s => s.id === sectionId
      ? { ...s, items: [...(s.items || []), newItem] }
      : s
    ));
    setSelectedItem({ sectionId, itemId, ...newItem });
  };

  const updateItem = (sectionId, itemId, updates) => {
    setSections(prev => prev.map(s => s.id === sectionId
      ? { ...s, items: s.items.map(i => i.id === itemId ? { ...i, ...updates } : i) }
      : s
    ));
    setSelectedItem(prev => prev?.itemId === itemId ? { ...prev, ...updates } : prev);
  };

  const deleteItem = (sectionId, itemId) => {
    setSections(prev => prev.map(s => s.id === sectionId
      ? { ...s, items: s.items.filter(i => i.id !== itemId) }
      : s
    ));
    if (selectedItem?.itemId === itemId) setSelectedItem(null);
  };

  const toggleAssetType = (type) => {
    setAssetTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const docId = isNew ? `tmpl-${Date.now()}` : templateId;
      await setDoc(doc(db, 'organisations', resolvedOrgId, 'checkTemplates', docId), {
        name: name.trim(),
        description: description.trim(),
        assetTypes,
        estimatedMinutes: Number(estimatedMinutes),
        frequency,
        dueByTime,
        sections,
        active: true,
        updatedAt: serverTimestamp(),
        ...(isNew && { createdAt: serverTimestamp() }),
      });

      await logAudit(resolvedOrgId, {
        action: isNew ? AUDIT_ACTIONS.TEMPLATE_CREATED : AUDIT_ACTIONS.TEMPLATE_UPDATED,
        performedBy: userProfile?.id,
        performedByName: userProfile?.name,
        targetId: docId,
        targetType: 'template',
        targetName: name.trim(),
        details: `${isNew ? 'Created' : 'Updated'} template "${name.trim()}" — ${frequency}, due by ${dueByTime}`,
        branchId: null,
      });

      navigate('/settings/templates');
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const fbColour = (fb) => {
    const found = FAIL_BEHAVIOURS.find(b => b.key === fb);
    return found?.colour || 'var(--color-text-tertiary)';
  };

  const selectedSection = selectedItem ? sections.find(s => s.id === selectedItem.sectionId) : null;
  const selectedItemData = selectedSection?.items?.find(i => i.id === selectedItem?.itemId);

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ display: 'flex', flex: 1, minHeight: '100vh', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '20px 28px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div onClick={() => navigate('/settings/templates')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 8 }}>
              <IconArrowLeft size={14} stroke={1.5} /> Templates
            </div>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Template name"
              style={{ fontSize: 20, fontWeight: 400, border: 'none', outline: 'none', background: 'transparent', width: '100%', color: 'var(--color-text-primary)', padding: 0 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 16, flexShrink: 0 }}>
            <button
              onClick={() => navigate('/settings/templates')}
              style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 20px', fontSize: 13, cursor: saving || !name.trim() ? 'not-allowed' : 'pointer', opacity: saving || !name.trim() ? 0.6 : 1 }}
            >
              {saving ? 'Saving...' : 'Save template'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Main editor */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>

            {/* Template settings */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 400, marginBottom: 14 }}>Template settings</div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Brief description of when this template is used"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Asset types</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {['vehicle', 'trailer', 'machinery'].map(type => (
                      <div
                        key={type}
                        onClick={() => toggleAssetType(type)}
                        style={{ padding: '5px 12px', borderRadius: 'var(--radius-md)', border: '0.5px solid', borderColor: assetTypes.includes(type) ? 'var(--color-brand)' : 'var(--color-border-medium)', background: assetTypes.includes(type) ? 'var(--color-brand-light)' : 'transparent', color: assetTypes.includes(type) ? 'var(--color-brand)' : 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer', textTransform: 'capitalize', transition: 'all 0.15s' }}
                      >
                        {type}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Estimated time (minutes)</label>
                  <input type="number" value={estimatedMinutes} onChange={e => setEstimatedMinutes(e.target.value)} min={1} max={60} style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>

              {/* Frequency and due-by time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Check frequency</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { key: 'daily', label: 'Daily' },
                      { key: 'weekly', label: 'Weekly' },
                    ].map(f => (
                      <div
                        key={f.key}
                        onClick={() => setFrequency(f.key)}
                        style={{ flex: 1, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: `0.5px solid ${frequency === f.key ? 'var(--color-brand)' : 'var(--color-border-medium)'}`, background: frequency === f.key ? 'var(--color-brand-light)' : 'transparent', color: frequency === f.key ? 'var(--color-brand)' : 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}
                      >
                        {f.label}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 5 }}>
                    {frequency === 'weekly' ? 'Must be completed by the due time every Friday' : 'Must be completed by the due time each day'}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                    Due by time
                    <span style={{ color: 'var(--color-text-tertiary)', marginLeft: 4 }}>— overdue reminder fires at this time</span>
                  </label>
                  <input
                    type="time"
                    value={dueByTime}
                    onChange={e => setDueByTime(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 5 }}>
                    Push notification sent to operator and branch manager if not completed by this time
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              {FAIL_BEHAVIOURS.map(fb => (
                <div key={fb.key} style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: fb.colour }} />
                  {fb.label}
                </div>
              ))}
            </div>

            {/* Sections */}
            {sections.map(section => (
              <div key={section.id} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: 6, border: '0.5px solid var(--color-border-light)' }}>
                  <IconGripVertical size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                  <input
                    type="text"
                    value={section.title}
                    onChange={e => updateSectionTitle(section.id, e.target.value)}
                    style={{ fontSize: 13, fontWeight: 400, flex: 1, border: 'none', outline: 'none', background: 'transparent', color: 'var(--color-text-primary)' }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginRight: 8 }}>{section.items?.length || 0} items</div>
                  <div onClick={() => deleteSection(section.id)} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--color-danger)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-tertiary)'}
                  >
                    <IconTrash size={13} stroke={1.5} />
                  </div>
                </div>

                {(section.items || []).map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedItem({ sectionId: section.id, itemId: item.id, ...item })}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', background: selectedItem?.itemId === item.id ? 'var(--color-brand-light)' : 'var(--color-bg-primary)', border: `0.5px solid ${selectedItem?.itemId === item.id ? 'var(--color-brand)' : 'var(--color-border-light)'}`, borderRadius: 'var(--radius-md)', marginBottom: 4, cursor: 'pointer', transition: 'all 0.15s' }}
                  >
                    <IconGripVertical size={13} color="var(--color-text-tertiary)" stroke={1.5} />
                    <div style={{ flex: 1, fontSize: 13, color: selectedItem?.itemId === item.id ? 'var(--color-brand)' : 'var(--color-text-primary)' }}>{item.label}</div>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#E1F5EE', color: '#0F6E56', flexShrink: 0 }}>
                      {ITEM_TYPES.find(t => t.key === item.type)?.label || 'Pass / Fail'}
                    </span>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: fbColour(item.failBehaviour), flexShrink: 0 }} />
                    <div onClick={e => { e.stopPropagation(); deleteItem(section.id, item.id); }} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--color-danger)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-tertiary)'}
                    >
                      <IconX size={13} stroke={1.5} />
                    </div>
                  </div>
                ))}

                <div
                  onClick={() => addItem(section.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', border: '0.5px dashed var(--color-border-medium)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: 12 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-medium)'}
                >
                  <IconPlus size={13} stroke={1.5} /> Add check item
                </div>
              </div>
            ))}

            <div
              onClick={addSection}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', border: '0.5px dashed var(--color-border-medium)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-medium)'}
            >
              <IconPlus size={15} stroke={1.5} /> Add section
            </div>
          </div>

          {/* Right panel */}
          <div style={{ width: 280, borderLeft: '0.5px solid var(--color-border-light)', padding: '20px', background: 'var(--color-bg-primary)', overflow: 'auto', flexShrink: 0 }}>
            {!selectedItemData ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 400, marginBottom: 10 }}>Check item</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                  Select a check item on the left to edit its settings here.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 400, marginBottom: 14 }}>Edit check item</div>

                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Item label</label>
                  <input
                    type="text"
                    value={selectedItemData.label}
                    onChange={e => updateItem(selectedItem.sectionId, selectedItem.itemId, { label: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Response type</label>
                  {ITEM_TYPES.map(type => (
                    <div
                      key={type.key}
                      onClick={() => updateItem(selectedItem.sectionId, selectedItem.itemId, { type: type.key })}
                      style={{ padding: '7px 10px', borderRadius: 'var(--radius-md)', border: `0.5px solid ${selectedItemData.type === type.key ? 'var(--color-brand)' : 'var(--color-border-light)'}`, background: selectedItemData.type === type.key ? 'var(--color-brand-light)' : 'transparent', color: selectedItemData.type === type.key ? 'var(--color-brand)' : 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer', marginBottom: 5, transition: 'all 0.15s' }}
                    >
                      {type.label}
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>If this item fails</label>
                  {FAIL_BEHAVIOURS.map(opt => (
                    <div
                      key={opt.key}
                      onClick={() => updateItem(selectedItem.sectionId, selectedItem.itemId, { failBehaviour: opt.key })}
                      style={{ border: `0.5px solid ${selectedItemData.failBehaviour === opt.key ? opt.colour : 'var(--color-border-light)'}`, borderRadius: 'var(--radius-md)', padding: '8px 10px', marginBottom: 6, cursor: 'pointer', background: selectedItemData.failBehaviour === opt.key ? opt.colour + '18' : 'transparent', display: 'flex', gap: 8, transition: 'all 0.15s' }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: opt.colour, flexShrink: 0, marginTop: 3 }} />
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="required-check"
                    checked={selectedItemData.required !== false}
                    onChange={e => updateItem(selectedItem.sectionId, selectedItem.itemId, { required: e.target.checked })}
                    style={{ width: 'auto' }}
                  />
                  <label htmlFor="required-check" style={{ fontSize: 12, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>Required — cannot skip</label>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}